import { useState } from "react";
import { createComment, type CreateCommentParams } from "@/api/comment";
import { toastSuccess, toastError } from "@/utils/toast";

const TYPE_TO_API: Record<string, CreateCommentParams["product_type"]> = {
  hotel: "1",
  tour: "2",
  car: "3",
  guide: "4",
};
const TYPE_LABEL: Record<string, string> = {
  hotel: "酒店评价",
  tour: "线路评价",
  car: "租车评价",
  guide: "导游评价",
};
const TYPE_ICON: Record<string, string> = {
  hotel: "\uD83C\uDFE8",
  tour: "\uD83D\uDDFA\uFE0F",
  car: "\uD83D\uDE97",
  guide: "\uD83D\uDC69\u200D\u2708\uFE0F",
};
const TAG_MAP: Record<string, string[]> = {
  hotel: ["服务周到", "设施完善", "位置便利", "性价比高", "卫生整洁"],
  tour: ["行程丰富", "导游专业", "景点精选", "餐食满意", "服务贴心"],
  car: ["车况良好", "司机专业", "准时接送", "车内整洁", "路线熟悉"],
  guide: ["专业知识丰富", "讲解生动", "态度亲切", "时间安排合理", "应变能力强"],
};
const STAR_LABELS = ["", "非常差", "较差", "一般", "不错", "非常好"];

export interface ReviewModalProps {
  visible: boolean;
  orderId?: number;
  productId?: number;
  productType?: string;
  productName?: string;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

export default function ReviewModal({
  visible,
  orderId,
  productId,
  productType,
  productName,
  onClose,
  onSubmitSuccess,
}: ReviewModalProps) {
  const [stars, setStars] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [content, setContent] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  if (!visible) return null;
  const typeKey = productType ?? "hotel";
  const tags = TAG_MAP[typeKey] ?? TAG_MAP.hotel;
  const toggleTag = (t: string) =>
    setSelectedTags((p) =>
      p.includes(t) ? p.filter((x) => x !== t) : [...p, t],
    );
  const canSubmit = stars > 0 && !submitting && !submitted;
  const handleSubmit = async () => {
    console.log(!canSubmit);
    console.log(orderId, productId, productType);
    if (!canSubmit || !orderId || !productId || !productType) return;
    setSubmitting(true);
    try {
      await createComment({
        order_id: orderId,
        product_id: productId,
        product_type: TYPE_TO_API[typeKey] ?? "1",
        product_score: stars,
        service_score: stars,
        comment: content.trim(),
        tags: selectedTags.length > 0 ? selectedTags : undefined,
      });
      setSubmitted(true);
      toastSuccess("评价提交成功");
      onSubmitSuccess?.();
    } catch (err: any) {
      toastError(err?.message || "提交失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="bg-white rounded-t-3xl w-full"
        style={{
          maxWidth: 480,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="flex items-center justify-between px-5 pt-4 pb-3 flex-shrink-0"
          style={{ borderBottom: "1px solid #F5F5F5" }}
        >
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F4F5F9" }}
          >
            <i
              className="ri-close-line"
              style={{ color: "#374151", fontSize: 18 }}
            />
          </button>
          <span className="text-base font-bold text-gray-900">评价服务</span>
          <div className="w-8" />
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {submitted ? (
            <div className="flex flex-col items-center py-10">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                style={{ background: "#FFF7ED" }}
              >
                <i
                  className="ri-star-fill"
                  style={{ color: "#F59E0B", fontSize: 32 }}
                />
              </div>
              <div className="text-base font-bold text-gray-900 mb-1">
                评价已提交！
              </div>
              <div className="text-xs text-gray-500 text-center mb-5">
                感谢您的宝贵意见
              </div>
              <button
                onClick={onClose}
                className="px-8 py-2.5 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
                style={{ background: "#F59E0B" }}
              >
                知道了
              </button>
            </div>
          ) : (
            <>
              {(productName || typeKey) && (
                <div
                  className="mb-5 px-3 py-3 rounded-xl flex items-center gap-3"
                  style={{
                    background: "#F8F8F8",
                    border: "1.5px solid #F0F0F0",
                  }}
                >
                  <span className="text-xl">{TYPE_ICON[typeKey]}</span>
                  <div className="min-w-0">
                    <div className="text-xs text-gray-400">
                      {TYPE_LABEL[typeKey] ?? "产品评价"}
                    </div>
                    <div className="text-sm font-medium text-gray-800 truncate">
                      {productName ?? "-"}
                    </div>
                  </div>
                </div>
              )}
              <div className="text-center mb-5">
                <div className="text-xs text-gray-400 mb-3">整体满意度</div>
                <div className="flex justify-center gap-2 mb-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button
                      key={i}
                      onMouseEnter={() => setHovered(i)}
                      onMouseLeave={() => setHovered(0)}
                      onClick={() => setStars(i)}
                      className="cursor-pointer w-11 h-11 flex items-center justify-center"
                    >
                      <i
                        className="ri-star-fill"
                        style={{
                          fontSize: 34,
                          color:
                            i <= (hovered || stars) ? "#F59E0B" : "#E5E7EB",
                        }}
                      />
                    </button>
                  ))}
                </div>
                {(hovered || stars) > 0 && (
                  <div
                    className="text-sm font-semibold"
                    style={{ color: "#F59E0B" }}
                  >
                    {STAR_LABELS[hovered || stars]}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-500 mb-2">
                  服务亮点（可多选）
                </div>
                <div className="flex flex-wrap gap-2">
                  {tags.map((t) => {
                    const a = selectedTags.includes(t);
                    return (
                      <button
                        key={t}
                        onClick={() => toggleTag(t)}
                        className="px-3 py-1.5 rounded-full text-xs cursor-pointer font-medium whitespace-nowrap transition-all"
                        style={{
                          background: a ? "#FFF7ED" : "#F4F5F9",
                          color: a ? "#D97706" : "#374151",
                          border: a
                            ? "1.5px solid #FED7AA"
                            : "1.5px solid transparent",
                        }}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mb-1">
                <div className="text-xs font-semibold text-gray-500 mb-2">
                  详细评价（选填）
                </div>
                <div className="relative">
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    maxLength={500}
                    placeholder="分享旅行体验..."
                    className="w-full rounded-2xl p-3 pr-12 text-xs resize-none outline-none"
                    style={{
                      background: "#F8F8F8",
                      border: "1.5px solid #F0F0F0",
                      minHeight: 100,
                      color: "#374151",
                    }}
                  />
                  <button
                    className="absolute right-3 bottom-3 w-9 h-9 rounded-full flex items-center justify-center cursor-pointer shadow-lg"
                    style={{
                      background: "linear-gradient(135deg,#FF6B35,#FF9A5C)",
                    }}
                  >
                    <i
                      className="ri-mic-line text-white"
                      style={{ fontSize: 16 }}
                    />
                  </button>
                </div>
                <div className="flex justify-end text-xs text-gray-400 mt-1 mb-4">
                  {content.length}/500
                </div>
              </div>
            </>
          )}
        </div>
        {!submitted && (
          <div
            className="px-5 py-3 flex-shrink-0"
            style={{ borderTop: "1px solid #F5F5F5", background: "#fff" }}
          >
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              style={{
                background: canSubmit ? "#F59E0B" : "#F0F0F0",
                color: canSubmit ? "#fff" : "#9CA3AF",
              }}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin" /> 提交中...
                </span>
              ) : (
                "提交评价"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
